import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useBranches } from '../../hooks/useBranches';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { usePermission } from '../../hooks/usePermission';
import { date, fine, today } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { DateInput, Drawer, Field, PercentInput, Select, TextInput } from '../../components/ui/Form';

interface JobWorkRow {
  id: string;
  doc_no: string;
  issue_date: string;
  status: string;
  vendor_name: string;
  issued_fine_wt: string;
  received_fine_wt: string | null;
  pieces: string;
}

interface JobLine {
  id: string;
  item_code: string;
  issued_fine_wt: string;
  issued_gross_wt: string;
  received_fine_wt: string | null;
  loss_fine_wt: string | null;
}

interface JobDetail {
  id: string;
  doc_no: string;
  status: string;
  allowed_loss_pct: string;
  lines: JobLine[];
}

interface Item {
  id: string;
  item_code: string;
  product_name: string;
  fine_wt: string;
}

const STATUS_TONE: Record<string, string> = {
  ISSUED: 'text-gold-700',
  PARTIALLY_RECEIVED: 'text-gold-700',
  RECEIVED: 'text-success',
  RECEIVED_WITH_LOSS: 'text-danger',
};

/**
 * Metal sent to an artisan and (most of it) coming back.
 *
 * While it is out, the gold sits in a job-work account, so the balance sheet
 * still says where it is. On receipt the loss is measured against the tolerance
 * agreed when it was issued; loss beyond that needs approval, and metal cannot
 * come back heavier than it went out — the schema refuses it.
 */
export function JobWorkPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const { branches, defaultBranchId } = useBranches();

  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const [weights, setWeights] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    vendorId: '',
    processType: 'MAKING',
    issueDate: today(),
    expectedDate: '',
    allowedLossPct: '2.00',
    remarks: '',
    branchId: '',
  });

  const list = useQuery({ queryKey: ['jobWorks'], queryFn: () => api.get<{ data: JobWorkRow[] }>('/job-works') });
  const suppliers = useQuery({ queryKey: ['suppliers', ''], queryFn: () => endpoints.parties.suppliers() });
  const items = useQuery({
    queryKey: ['items', 'IN_STOCK', form.branchId],
    queryFn: () =>
      endpoints.inventory.items({ status: 'IN_STOCK', branchId: form.branchId || undefined, limit: '200' }),
    enabled: open,
  });
  const detail = useQuery({
    queryKey: ['jobWork', detailId],
    queryFn: () => api.get<JobDetail>(`/job-works/${detailId}`),
    enabled: detailId !== null,
  });

  // Artisans are suppliers of the ARTISAN kind, but any supplier can do job work.
  const vendorList = (suppliers.data?.data ?? []) as unknown as { id: string; name: string }[];
  const itemList = (items.data?.data ?? []) as unknown as Item[];

  const issue = useMutation({
    mutationFn: () =>
      api.post(
        '/job-works',
        {
          branchId: form.branchId || defaultBranchId,
          issueDate: form.issueDate,
          ...(form.expectedDate ? { expectedDate: form.expectedDate } : {}),
          vendorId: form.vendorId,
          processType: form.processType,
          itemIds: chosen,
          ...(form.allowedLossPct ? { allowedLossPct: form.allowedLossPct } : {}),
          ...(form.remarks ? { remarks: form.remarks } : {}),
        },
        { 'idempotency-key': crypto.randomUUID() },
      ),
    onSuccess: () => {
      setOpen(false);
      setChosen([]);
      setError(null);
      void client.invalidateQueries({ queryKey: ['jobWorks'] });
      void client.invalidateQueries({ queryKey: ['items'] });
    },
    onError: setError,
  });

  const receive = useMutation({
    mutationFn: (jobWorkId: string) =>
      api.post(`/job-works/${jobWorkId}/receive`, {
        receiptDate: today(),
        lines: detail
          .data!.lines.filter((l) => weights[l.id])
          .map((l) => ({ lineId: l.id, receivedFineWt: weights[l.id]! })),
      }),
    onSuccess: () => {
      setError(null);
      setWeights({});
      void client.invalidateQueries({ queryKey: ['jobWorks'] });
      void client.invalidateQueries({ queryKey: ['jobWork'] });
      void client.invalidateQueries({ queryKey: ['items'] });
    },
    onError: setError,
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    issue.mutate();
  };

  const toggle = (id: string): void => setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const rows = list.data?.data ?? [];
  const chosenFine = itemList.filter((i) => chosen.includes(i.id)).reduce((a, i) => a + Number(i.fine_wt), 0);

  return (
    <>
      <PageHeader
        title="Job work"
        subtitle="Metal with an artisan is shown on the balance sheet as exactly that — out, not gone."
        actions={
          can('jobwork.issue') ? (
            <button className="btn-primary" onClick={() => setOpen(true)}>
              Issue to artisan
            </button>
          ) : null
        }
      />
      <ErrorNotice error={error} />

      {list.isLoading ? <Loading /> : null}
      {!list.isLoading && rows.length === 0 ? (
        <Empty
          message="Nothing out with an artisan."
          hint="Metal sent for making, polishing or setting stays on your balance sheet while it is out."
          action={can('jobwork.issue') ? { label: 'Issue to artisan', onClick: () => setOpen(true) } : undefined}
        />
      ) : null}

      {rows.length > 0 ? (
        <Table head={['Date', 'Document', 'Artisan', 'Pieces', 'Issued', 'Received', 'Status', '']}>
          {rows.map((j) => (
            <tr key={j.id}>
              <td className="td">{date(j.issue_date)}</td>
              <td className="td num cursor-pointer underline" onClick={() => setDetailId(j.id)}>
                {j.doc_no}
              </td>
              <td className="td">{j.vendor_name}</td>
              <td className="td num text-right">{j.pieces}</td>
              <td className="td num text-right">{fine(j.issued_fine_wt)}</td>
              <td className="td num text-right">{j.received_fine_wt ? fine(j.received_fine_wt) : '—'}</td>
              <td className={`td text-xs ${STATUS_TONE[j.status] ?? ''}`}>
                {j.status.replace(/_/g, ' ').toLowerCase()}
              </td>
              <td className="td">
                {(j.status === 'ISSUED' || j.status === 'PARTIALLY_RECEIVED') && can('jobwork.receive') ? (
                  <button className="btn-primary py-1 text-xs" onClick={() => setDetailId(j.id)}>
                    Receive
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </Table>
      ) : null}

      {open ? (
        <Drawer
          title="Issue to an artisan"
          subtitle="Choose the pieces going out and the loss you have agreed. The metal leaves stock and moves to the job-work account."
          onClose={() => setOpen(false)}
        >
          <ErrorNotice error={error} />
          <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Artisan" required>
                <Select
                  value={form.vendorId}
                  onChange={(v) => setForm({ ...form, vendorId: v })}
                  options={vendorList.map((s) => ({ value: s.id, label: s.name }))}
                  placeholder={vendorList.length === 0 ? 'Add a supplier first' : 'Choose…'}
                />
              </Field>
              <Field label="Work being done" required>
                <Select
                  value={form.processType}
                  onChange={(v) => setForm({ ...form, processType: v })}
                  options={[
                    { value: 'MAKING', label: 'Making' },
                    { value: 'POLISHING', label: 'Polishing' },
                    { value: 'SETTING', label: 'Stone setting' },
                    { value: 'REPAIR', label: 'Repair' },
                    { value: 'PLATING', label: 'Plating' },
                    { value: 'ENGRAVING', label: 'Engraving' },
                  ]}
                />
              </Field>
            </div>

            {branches.length > 1 ? (
              <Field label="From branch" required>
                <Select
                  value={form.branchId || (defaultBranchId ?? '')}
                  onChange={(v) => {
                    setForm({ ...form, branchId: v });
                    setChosen([]);
                  }}
                  options={branches.map((b) => ({ value: b.id, label: b.name }))}
                />
              </Field>
            ) : null}

            <div className="grid grid-cols-3 gap-3">
              <Field label="Issued" required>
                <DateInput value={form.issueDate} onChange={(v) => setForm({ ...form, issueDate: v })} />
              </Field>
              <Field label="Expected back">
                <DateInput value={form.expectedDate} onChange={(v) => setForm({ ...form, expectedDate: v })} />
              </Field>
              <Field label="Loss allowed" hint="Beyond this needs approval.">
                <PercentInput
                  value={form.allowedLossPct}
                  onChange={(v) => setForm({ ...form, allowedLossPct: v })}
                  placeholder="2.00"
                />
              </Field>
            </div>

            <Field label="Remarks">
              <TextInput value={form.remarks} onChange={(v) => setForm({ ...form, remarks: v })} />
            </Field>

            <div>
              <p className="label">Pieces to send</p>
              {items.isLoading ? <Loading /> : null}
              <div className="max-h-64 space-y-1 overflow-y-auto rounded border border-ink/15 bg-white p-2">
                {itemList.length === 0 ? (
                  <p className="p-2 text-sm text-ink-soft">Nothing in stock to send.</p>
                ) : (
                  itemList.map((i) => (
                    <label key={i.id} className="flex items-center gap-2 rounded p-1 text-sm hover:bg-gold-50">
                      <input type="checkbox" checked={chosen.includes(i.id)} onChange={() => toggle(i.id)} />
                      <span className="num">{i.item_code}</span>
                      <span className="text-ink-soft">{i.product_name}</span>
                      <span className="num ml-auto">{fine(i.fine_wt)}</span>
                    </label>
                  ))
                )}
              </div>
              {chosen.length > 0 ? (
                <p className="mt-2 text-sm">
                  {chosen.length} piece{chosen.length === 1 ? '' : 's'},{' '}
                  <span className="num font-medium">{fine(chosenFine.toFixed(4))}</span>
                </p>
              ) : null}
            </div>

            <button className="btn-primary w-full" disabled={chosen.length === 0 || !form.vendorId || issue.isPending}>
              {issue.isPending ? 'Issuing…' : 'Issue to artisan'}
            </button>
          </form>
        </Drawer>
      ) : null}

      {detailId && detail.data ? (
        <Drawer
          title={`Job work ${detail.data.doc_no}`}
          subtitle={`${detail.data.status.replace(/_/g, ' ').toLowerCase()} · ${detail.data.allowed_loss_pct}% loss allowed`}
          onClose={() => {
            setDetailId(null);
            setWeights({});
          }}
        >
          <ErrorNotice error={error} />

          <table className="w-full text-sm">
            <thead>
              <tr className="border-y border-ink/20 text-xs uppercase tracking-wide text-ink-soft">
                <th className="py-1 text-left">Item</th>
                <th className="py-1 text-right">Issued</th>
                <th className="py-1 text-right">{detail.data.status === 'RECEIVED' ? 'Received' : 'Weigh back'}</th>
              </tr>
            </thead>
            <tbody>
              {detail.data.lines.map((l) => (
                <tr key={l.id} className="border-b border-ink/10">
                  <td className="num py-1">{l.item_code}</td>
                  <td className="num py-1 text-right">{fine(l.issued_fine_wt)}</td>
                  <td className="py-1 text-right">
                    {l.received_fine_wt ? (
                      <span className="num">
                        {fine(l.received_fine_wt)}
                        {l.loss_fine_wt && Number(l.loss_fine_wt) > 0 ? (
                          <span className="block text-xs text-ink-soft">loss {l.loss_fine_wt} g</span>
                        ) : null}
                      </span>
                    ) : (
                      <input
                        className="field num w-28 px-2 py-1 text-right text-sm"
                        inputMode="decimal"
                        placeholder={l.issued_fine_wt}
                        value={weights[l.id] ?? ''}
                        onChange={(e) => setWeights({ ...weights, [l.id]: e.target.value.replace(/[^\d.]/g, '') })}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {(detail.data.status === 'ISSUED' || detail.data.status === 'PARTIALLY_RECEIVED') &&
          can('jobwork.receive') ? (
            <>
              <p className="mt-3 text-xs text-ink-soft">
                Weigh each piece as it comes back. Loss beyond the {detail.data.allowed_loss_pct}% agreed will be held
                for approval. A piece cannot weigh more than it did going out.
              </p>
              <button
                className="btn-primary mt-3 w-full"
                disabled={Object.keys(weights).length === 0 || receive.isPending}
                onClick={() => receive.mutate(detail.data.id)}
              >
                {receive.isPending ? 'Receiving…' : 'Receive from artisan'}
              </button>
            </>
          ) : null}
        </Drawer>
      ) : null}
    </>
  );
}
